import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {BehaviorSubject, Observable} from 'rxjs';
import {IPermissionPerCompany} from '@pages/permissions/interfaces';
import {environment} from '../../../../environments/environment';
import {JsonResponse} from "@core_interfaces/JsonResponse.interface";

@Injectable({providedIn: 'root'})
export class PermissionAccessService {
  private permissionsSubject: BehaviorSubject<IPermissionPerCompany[]> =
    new BehaviorSubject<IPermissionPerCompany[]>([]);

  public permissions$: Observable<IPermissionPerCompany[]> =
    this.permissionsSubject.asObservable();

  constructor(
    private _http: HttpClient,
  ) { }

  public initializeAuthUserPermissions(): Observable<JsonResponse> {
    return this._http.get<JsonResponse>('/permissions/general/auth-user');
  }

  public getAuthUserPermissions(): void {
    const headers: HttpHeaders = new HttpHeaders().set('skipHttpInterceptorLoader', 'true');

    this._http
      .get<JsonResponse>('/permissions/general/auth-user', {headers})
      .subscribe({
        next: (response: JsonResponse): void => {
          this.updateAuthUserPermissions(
            (response.data as IPermissionPerCompany[]) || [],
          );
        },
        error: (err: any): void =>
          console.error('Error fetching permissions:', err),
      });
  }

  public updateAuthUserPermissions(permissions: IPermissionPerCompany[]): void {
    this.permissionsSubject.next(permissions);
  }

  public hasSinglePermission = (
    feature: string,
    permission: string,
    companyOverride?: string,
    platformOverride?: string,
  ): boolean => {
    const company: string = companyOverride || environment.company || '';
    const platform: string = platformOverride || environment.platform || '';

    if (!company || !platform) {
      throw new Error('PermissionAccessService: Company or platform not provided, check the environment file');
    }

    const data: IPermissionPerCompany[] = this.permissionsSubject.getValue();

    for (const comp of data) {
      if (comp.companySlug === company) {
        for (const plat of comp.platforms) {
          if (plat.platformSlug === platform) {
            for (const gr of plat.groups) {
              if (gr.groupSlug === feature) {
                for (const perm of gr.permissions) {
                  if (perm.permissionSlug === permission) {
                    return true;
                  }
                }
              }
            }
          }
        }
      }
    }

    return false;
  };

  public hasAllPermissions = (
    feature: string,
    permissions: string[],
    companyOverride?: string,
    platformOverride?: string,
  ): boolean => {
    const company: string = companyOverride || environment.company || '';
    const platform: string = platformOverride || environment.platform || '';

    if (!company || !platform) {
      throw new Error('PermissionAccessService: Company or platform not provided, check the environment file');
    }

    const data: IPermissionPerCompany[] = this.permissionsSubject.getValue();

    for (const comp of data) {
      if (comp.companySlug === company) {
        for (const plat of comp.platforms) {
          if (plat.platformSlug === platform) {
            for (const gr of plat.groups) {
              if (gr.groupSlug === feature) {
                let hasAll: boolean = true;
                for (const permissionSlug of permissions) {
                  let found: boolean = false;
                  for (const perm of gr.permissions) {
                    if (perm.permissionSlug === permissionSlug) {
                      found = true;
                      break;
                    }
                  }

                  if (!found) {
                    hasAll = false;
                    break;
                  }
                }

                if (hasAll) {
                  return true;
                }
              }
            }
          }
        }
      }
    }

    return false;
  };

  public hasAnyPermission = (
    feature: string,
    permissions: string[],
    companyOverride?: string,
    platformOverride?: string,
  ): boolean => {
    const company: string = companyOverride || environment.company || '';
    const platform: string = platformOverride || environment.platform || '';

    if (!company || !platform) {
      throw new Error('PermissionAccessService: Company or platform not provided, check the environment file');
    }

    const data: IPermissionPerCompany[] = this.permissionsSubject.getValue();
    for (const comp of data) {
      if (comp.companySlug === company) {
        for (const plat of comp.platforms) {
          if (plat.platformSlug === platform) {
            for (const gr of plat.groups) {
              if (gr.groupSlug === feature) {
                for (const permissionSlug of permissions) {
                  for (const perm of gr.permissions) {
                    if (perm.permissionSlug === permissionSlug) {
                      return true;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    return false;
  };
}

export enum PermissionCheckMode {
  ALL = 'all',
  ANY = 'any',
  SINGLE = 'single',
}

export const _ResourceDeniedErrorCode = 'FORBIDDEN';
